import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { Trip, Stop, TripCalculation } from '@/types'
import { tripService } from '@/services/trip'

export const useTripStore = defineStore('trip', () => {
  const trips = ref<Trip[]>([])
  const currentTrip = ref<Trip | null>(null)
  const isLoading = ref(false)
  const calculation = ref<TripCalculation | null>(null)

  const sortedStops = computed(() => {
    if (!currentTrip.value) return []
    return [...currentTrip.value.stops].sort((a, b) => a.order - b.order)
  })

  async function loadTrips(userId: string) {
    isLoading.value = true
    try {
      trips.value = await tripService.getTrips(userId)
    } finally {
      isLoading.value = false
    }
  }

  async function loadTrip(id: string) {
    isLoading.value = true
    try {
      currentTrip.value = await tripService.getTripById(id)
      if (currentTrip.value) {
        localStorage.setItem('lastTripId', currentTrip.value.id)
        await calculateTrip()
      }
    } finally {
      isLoading.value = false
    }
  }

  async function createTrip(name: string, userId: string, vehicleId?: string) {
    isLoading.value = true
    try {
      const trip = await tripService.createTrip(name, userId, vehicleId)
      trips.value.unshift(trip)
      currentTrip.value = trip
      localStorage.setItem('lastTripId', trip.id)
      return trip
    } finally {
      isLoading.value = false
    }
  }

  async function updateTrip(updates: Partial<Trip>) {
    if (!currentTrip.value) return

    await tripService.updateTrip(currentTrip.value.id, updates)
    currentTrip.value = { ...currentTrip.value, ...updates }
    
    const index = trips.value.findIndex(t => t.id === currentTrip.value!.id)
    if (index !== -1) {
      trips.value[index] = currentTrip.value
    }
  }

  async function deleteTrip(id: string) {
    await tripService.deleteTrip(id)
    trips.value = trips.value.filter(t => t.id !== id)
    if (currentTrip.value?.id === id) {
      currentTrip.value = null
    }
  }

  async function addStop(stop: Omit<Stop, 'id' | 'createdAt' | 'updatedAt'>) {
    console.log('📍 addStop called')
    console.log('📍 currentTrip:', currentTrip.value)
    console.log('📍 stop data:', stop)
    
    if (!currentTrip.value) {
      console.error('❌ No current trip!')
      const error = new Error('No active trip. Please create or select a trip first.')
      error.name = 'NoTripError'
      throw error
    }

    console.log('📍 Calling tripService.addStop...')
    const newStop = await tripService.addStop(currentTrip.value.id, stop)
    console.log('✅ New stop created:', newStop)
    
    currentTrip.value.stops.push(newStop)
    console.log('📍 Stop added to currentTrip.stops. Total stops:', currentTrip.value.stops.length)
    
    try {
      await calculateTrip()
      console.log('📍 Trip recalculated')
    } catch (error) {
      console.error('⚠️ Trip recalculation failed after adding stop:', error)
    }
    
    return newStop
  }

  async function updateStop(id: string, updates: Partial<Stop>) {
    if (!currentTrip.value) return

    await tripService.updateStop(id, updates)
    const index = currentTrip.value.stops.findIndex(s => s.id === id)
    if (index !== -1) {
      currentTrip.value.stops[index] = { ...currentTrip.value.stops[index], ...updates }
    }
    await calculateTrip()
  }

  async function deleteStop(id: string) {
    if (!currentTrip.value) return

    await tripService.deleteStop(id)
    currentTrip.value.stops = currentTrip.value.stops.filter(s => s.id !== id)
    await calculateTrip()
  }

  async function reorderStops(stopIds: string[]) {
    if (!currentTrip.value) return

    for (let i = 0; i < stopIds.length; i++) {
      const stop = currentTrip.value.stops.find(s => s.id === stopIds[i])
      if (stop) {
        await updateStop(stop.id, { order: i })
      }
    }
    await calculateTrip()
  }

  async function calculateTrip() {
    if (!currentTrip.value || currentTrip.value.stops.length < 2) {
      calculation.value = null
      return
    }

    // Pass fuel consumption and price to the calculation
    calculation.value = await tripService.calculateRoute(
      sortedStops.value,
      currentTrip.value.fuelConsumption,
      currentTrip.value.fuelPrice
    )
    
    // Update trip with calculated values
    await updateTrip({
      totalDistance: calculation.value.totalDistance,
      totalDuration: calculation.value.totalDuration,
      totalCost: calculation.value.totalFuelCost
    })
  }

  function setCurrentTrip(trip: Trip | null) {
    currentTrip.value = trip
    if (trip) {
      localStorage.setItem('lastTripId', trip.id)
    }
  }

  function clearData() {
    trips.value = []
    currentTrip.value = null
    calculation.value = null
    localStorage.removeItem('lastTripId')
  }

  async function initialize(userId: string) {
    console.log('[Trip Store] Initializing trip store for user:', userId)
    try {
      await loadTrips(userId)
      console.log('[Trip Store] Loaded trips:', trips.value.length)
      
      // Restore last active trip
      const lastTripId = localStorage.getItem('lastTripId')
      console.log('[Trip Store] Last trip ID from localStorage:', lastTripId)
      
      if (lastTripId) {
        const lastTrip = trips.value.find(t => t.id === lastTripId)
        if (lastTrip) {
          console.log('[Trip Store] Restoring last trip:', lastTrip.name)
          await loadTrip(lastTripId)
          return
        }
      }
      
      // If no last trip or not found, select first trip
      if (trips.value.length > 0) {
        console.log('[Trip Store] Selecting first trip:', trips.value[0].name)
        currentTrip.value = trips.value[0]
        localStorage.setItem('lastTripId', trips.value[0].id)
      } else {
        console.log('[Trip Store] No trips found')
      }
    } catch (error) {
      console.error('[Trip Store] Error initializing:', error)
    }
  }

  return {
    trips,
    currentTrip,
    isLoading,
    calculation,
    sortedStops,
    initialize,
    loadTrips,
    loadTrip,
    createTrip,
    updateTrip,
    deleteTrip,
    addStop,
    updateStop,
    deleteStop,
    reorderStops,
    calculateTrip,
    setCurrentTrip,
    clearData
  }
})